import { dispatchAction } from '@ardoq/rxbeach';
import type { ComponentSelection } from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { showToast, ToastType } from '@ardoq/status-ui';
import { registerLoadedState } from 'loadedState/actions';
import { buildTraversalState, isError } from 'loadedState/buildState';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import { enterViewpointMode } from 'streams/traversals/actions';
import { scopeDataLoaded } from 'traversals/stagedLoadedDataAndState$';
import { getDatasetsListI18n } from './getImprovedDatasetsPanelI18n';
import { datasetTracking } from 'loadedState/datasetTracking';
import { improvedDatasetsTracking } from './improvedDatasetsTracking';

const reportCreateFailed = (error: unknown): false => {
  logError(
    error instanceof Error ? error : new Error(String(error)),
    'Could not create the new dataset.'
  );
  // i18nLocale$.state OK here: imperative one-shot error toast
  showToast(
    getDatasetsListI18n(i18nLocale$.state).createFailed,
    ToastType.ERROR
  );
  return false;
};

type RegisterNewDatasetArgs = {
  uuid: string;
  componentSelection: ComponentSelection;
};

export const registerNewDataset = async ({
  uuid,
  componentSelection,
}: RegisterNewDatasetArgs): Promise<boolean> => {
  try {
    const traversalParams = {
      ...loadedStateOperations.traversalParamsFromSelectionAndPaths(
        componentSelection,
        []
      ),
      uuid,
    };
    const loadedGraphOrError = await buildTraversalState(
      loadedStateOperations.toTraversalParams(traversalParams)
    );
    if (isError(loadedGraphOrError)) {
      improvedDatasetsTracking.failedToBuildNewDataset(
        loadedGraphOrError.reason
      );
      return reportCreateFailed(new Error(loadedGraphOrError.error));
    }
    dispatchAction(
      scopeDataLoaded({
        trackingLocation: 'viewpointNavigator',
        scopeData: loadedGraphOrError.scopeData,
      })
    );
    dispatchAction(enterViewpointMode());
    const loadedState = loadedStateOperations.attachLoadedGraph(
      traversalParams,
      loadedGraphOrError
    );
    dispatchAction(registerLoadedState({ loadedStates: [loadedState] }));
    datasetTracking.datasetApplied({
      surface: 'sidebar_panel',
      trigger: 'create',
      paths: traversalParams.data.paths,
    });
    return true;
  } catch (error) {
    improvedDatasetsTracking.failedToRegisterNewDataset();
    return reportCreateFailed(error);
  }
};
